import { defineStore } from 'pinia';
import { ref } from 'vue';

import { play, type SoundName } from '@/audio';

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error';

/**
 * Which plate a toast is painted on. One per banner in the
 * notifications_quick sheet; a toast that names no kind gets one derived
 * from its level, so every call site works untouched.
 */
export type NotificationKind =
  | 'scroll'
  | 'battle'
  | 'shield'
  | 'castle'
  | 'letter'
  | 'horn'
  | 'grain'
  | 'alert';

export interface Notification {
  id: number;
  level: NotificationLevel;
  message: string;
  kind: NotificationKind;
}

let nextId = 1;

export const useNotificationStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([]);

  /** Every toast carries a sound, so feedback is heard as well as seen. */
  const SOUND_FOR: Record<NotificationLevel, SoundName> = {
    success: 'confirm',
    error: 'error',
    warning: 'cancel',
    info: 'clickSoft',
  };

  const KIND_FOR: Record<NotificationLevel, NotificationKind> = {
    success: 'scroll',
    error: 'alert',
    warning: 'alert',
    info: 'scroll',
  };

  function push(
    level: NotificationLevel,
    message: string,
    ttlMs = 6000,
    kind?: NotificationKind,
  ): number {
    play(SOUND_FOR[level]);
    const id = nextId++;
    items.value.push({ id, level, message, kind: kind ?? KIND_FOR[level] });
    // Never let the stack grow past what fits on screen.
    if (items.value.length > 5) items.value.shift();
    window.setTimeout(() => dismiss(id), ttlMs);
    return id;
  }

  function dismiss(id: number): void {
    items.value = items.value.filter((n) => n.id !== id);
  }

  return {
    items,
    dismiss,
    info: (m: string, kind?: NotificationKind) => push('info', m, 6000, kind),
    success: (m: string, kind?: NotificationKind) => push('success', m, 6000, kind),
    warning: (m: string, kind?: NotificationKind) => push('warning', m, 6000, kind),
    error: (m: string, kind?: NotificationKind) => push('error', m, 9000, kind),
  };
});
